import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronDown } from '../../../icons';
import { Link } from 'react-router-dom';
import './ProfileDropdown.scss';

interface ProfileDropdownProps {
  userInitials: string;
  displayName: string;
  profileImage?: string;
  loading: boolean;
  onLogout: (e: React.MouseEvent) => void;
}

const ProfileDropdown: React.FC<ProfileDropdownProps> = ({
  userInitials,
  displayName,
  profileImage,
  loading,
  onLogout
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        setIsOpen(!isOpen);
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div
      ref={dropdownRef}
      className="profile-dropdown"
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <motion.div
        className={`profile-trigger ${isOpen ? 'open' : ''}`}
        onClick={handleToggle}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {/* Profile Avatar */}
        {loading ? (
          <div className="user-avatar">
            <div style={{
              width: '20px',
              height: '20px',
              border: '2px solid #6c757d',
              borderTop: '2px solid transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
          </div>
        ) : profileImage ? (
          <div className="user-avatar">
            <img
              src={profileImage}
              alt="User"
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '10px',
                objectFit: 'cover'
              }}
            />
          </div>
        ) : (
          <div className="user-avatar">
            <h2 style={{ fontSize: '16px', margin: 0, color: 'white', fontWeight: 'bold' }}>
              {userInitials || 'U'}
            </h2>
          </div>
        )}

        <span className="user-name">
          {loading ? "Loading..." : displayName}
        </span>

        <motion.div
          className="dropdown-arrow"
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <FiChevronDown />
        </motion.div>
      </motion.div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="profile-dropdown-menu"
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            <motion.div
              className="dropdown-item"
              onClick={() => setIsOpen(false)}
              whileHover={{ backgroundColor: '#f8f9fa' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.1 }}
            >
              <Link to="/profile">Profile</Link>
            </motion.div>
            <motion.div
              className="dropdown-item"
              onClick={(e) => {
                setIsOpen(false);
                onLogout(e);
              }}
              whileHover={{ backgroundColor: '#f8f9fa' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.1, delay: 0.02 }}
            >
              <span>Logout</span>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProfileDropdown;
